<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate';
import {
  BModal, BButton, BAlert, BRow, BCol, BFormGroup, BFormCheckbox, AlertPlugin,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import axios from '@/libs/axios';
import { required, email } from '@validations';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

extend('date_after_or_equal', {
  params: ['target'],
  validate(value, { target }) {
    if (!value || !target) return true;
    const valueDate = new Date(value);
    const targetDate = new Date(target);
    return valueDate >= targetDate;
  },
  message: 'A data final deve ser igual ou posterior à data inicial',
});

export default {
  components: {
    BModal,
    BButton,
    BAlert,
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        comunicadoid: '',
        comunicadotitulo_ptbr: '',
        comunicadotitulo_enus: '',
        comunicadotitulo_eses: '',
        comunicadoconteudo_ptbr: '',
        comunicadoconteudo_enus: '',
        comunicadoconteudo_eses: '',
        comunicadodatainicial: '',
        comunicadodatafinal: '',
        tipocomunicadoid: '',
        comunicadoativo: '',
        comunicadotipotexto: '',
      }),
      require: true,
    },
    titleModal: {
      type: String,
      default: null,
      require: false,
    },
    idModal: {
      type: String,
      default: null,
      require: false,
    },
    labelBtnOk: {
      type: String,
      default: null,
      require: false,
    },
  },
  data() {
    return {
      isFormValid: false,
      dataFromApiTipoComunicado: [],
      hasSubmitted: false,
    };
  },
  computed: {
    dataForm: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  mounted() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === this.idModal) {
        this.onModalShow();
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::show');
  },
  methods: {
    onModalShow() {
      this.fetchDataFromTipoComunicado();
      this.hasSubmitted = false;
      this.$nextTick(() => {
        if (this.$refs.refFormData) {
          this.$refs.refFormData.reset();
        }
      });
    },
    async validateForm() {
      this.hasSubmitted = true;
      const valid = await this.$refs.refFormData.validate();

      if (valid) {
        this.$emit('ok', this.dataForm);
        this.$bvModal.hide(this.idModal);
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            text: 'Preencha todos os campos corretamente.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
      }
    },
    async fetchDataFromTipoComunicado() {
      try {
        const response = await axios.get('/tipo-comunicado');
        this.dataFromApiTipoComunicado = response.data.data.map((option) => ({
          value: option.tipocomunicadoid,
          text: `${option.tipocomunicadoid} - ${option.tipocomunicadodescricao}`,
        }));
      } catch (error) {
        console.error('Erro ao chamar a API:', error);
      }
    },
    async checkFormValidity() {
      if (this.hasSubmitted) {
        const valid = await this.$refs.refFormData.validate();
        this.isFormValid = valid;
      }
    },
    getFieldState(errors) {
      if (!this.hasSubmitted) return null;
      return errors.length > 0 ? false : null;
    },
    onDateChange() {
      this.checkFormValidity();
      this.$nextTick(() => {
        if (this.$refs.refFormData) {
          this.$refs.refFormData.validate();
        }
      });
    },
  },
};
</script>

<template>
  <b-modal
      no-close-on-backdrop
      no-close-on-esc
      size="xl"
      :id="idModal"
      :title="titleModal"
      :ok-title="labelBtnOk"
      ok-variant="success"
      cancel-variant="secondary"
      body-bg-variant="light"
      cancel-title="Cancelar"
      @ok.prevent="validateForm"
      @cancel="$emit('cancel')"
      @close="$emit('cancel')"
  >
    <validation-observer ref="refFormData" v-slot="{ validate }">
      <b-form @submit.prevent>
        <b-row>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Código"
                label-for="comunicadoid"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Código"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadoid"
                    id="comunicadoid"
                    :state="getFieldState(errors)"
                    placeholder="Digite o código"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Titulo PTBR"
                label-for="comunicadotitulo_ptbr"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Titulo PTBR"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadotitulo_ptbr"
                    id="comunicadotitulo_ptbr"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Titulo PTBR"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Titulo Enus"
                label-for="comunicadotitulo_enus"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Titulo Enus"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadotitulo_enus"
                    id="comunicadotitulo_enus"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Titulo Enus"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Título Eses"
                label-for="comunicadotitulo_eses"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Título Eses"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadotitulo_eses"
                    id="comunicadotitulo_eses"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Titulo Eses"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Conteúdo PTBR"
                label-for="comunicadoconteudo_ptbr"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Conteúdo PTBR"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadoconteudo_ptbr"
                    id="comunicadoconteudo_ptbr"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Conteúdo PTBR"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Conteúdo Enus"
                label-for="comunicadoconteudo_enus"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Conteúdo Enus"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadoconteudo_enus"
                    id="comunicadoconteudo_enus"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Conteúdo Enus"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Conteúdo Eses"
                label-for="comunicadoconteudo_eses"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Conteúdo Eses"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadoconteudo_eses"
                    id="comunicadoconteudo_eses"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Conteúdo Eses"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Data Inicial"
                label-for="comunicadodatainicial"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Data Inicial"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadodatainicial"
                    id="comunicadodatainicial"
                    :state="getFieldState(errors)"
                    :disabled="dataForm.isDefault"
                    @input="onDateChange"
                    type="date"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Data Final"
                label-for="comunicadodatafinal"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Data Final"
                  :rules="`required|date_after_or_equal:${dataForm.comunicadodatainicial}`"
              >
                <b-form-input
                    v-model="dataForm.comunicadodatafinal"
                    id="comunicadodatafinal"
                    :state="getFieldState(errors)"
                    :disabled="dataForm.isDefault"
                    @input="onDateChange"
                    type="date"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Tipo Comunicado"
                label-for="tipocomunicadoid"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Tipo Comunicado"
                  rules="required"
              >
                <v-select v-model="dataForm.tipocomunicadoid" id="tipocomunicadoid" :options="dataFromApiTipoComunicado"
                  :disabled="dataForm.isDefault" label="text" :reduce="option => option.value" :clearable="false"
                  placeholder="Selecione" @input="checkFormValidity" :state="getFieldState(errors)" />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Status"
                label-for="comunicadoativo"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Status"
                  rules="required"
              >
                <b-form-checkbox
                    v-model="dataForm.comunicadoativo"
                    id="comunicadoativo"
                    :state="getFieldState(errors)"
                    :disabled="dataForm.isDefault"
                    @change="checkFormValidity"
                    :value="1"
                    :unchecked-value="0"
                >
                  Ativo
                </b-form-checkbox>
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
          <b-col
              cols="6"
              class="mb-1"
          >
            <b-form-group
                label="Tipo Texto"
                label-for="comunicadotipotexto"
            >
              <validation-provider
                  v-slot="{ errors }"
                  name="Tipo Texto"
                  rules="required"
              >
                <b-form-input
                    v-model="dataForm.comunicadotipotexto"
                    id="comunicadotipotexto"
                    :state="getFieldState(errors)"
                    placeholder="Digite o Tipo Texto"
                    :disabled="dataForm.isDefault"
                    @input="checkFormValidity"
                />
                <small v-if="hasSubmitted" class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-modal>
</template>
